import { useState } from "react";
import { useTranslation } from "@helpers/translate";
import Input from "../shared/Input";
import GoogleButton from "../../GoogleButton";
import FormButton from "../shared/FormButton";
import BinifyLogo from "@components/library/Icons/BinifyLogo";
import Checkbox from "../shared/Checkbox";
import {
  getEmailStatus,
  getEmailMessage,
  getPasswordStatus,
  getPasswordMessage,
  isEmailValid,
  isPasswordValid,
} from "@utils/validation";
import "../AuthForm.sass";
import { useDispatch } from "react-redux";
import { registration } from "@actions/auth";

const GOOGLE_AUTH_URL = "https://api.binify.app/api/auth/google";

const LEGAL_LINKS = {
  termsOfUse: "/docs/legal/terms-of-use.pdf",
  privacyPolicy: "/docs/legal/privacy-policy.pdf",
  cookiePolicy: "/docs/legal/cookie-policy.pdf",
};

const EMAIL_INPUT_ID = "signup-email";
const PASSWORD_INPUT_ID = "signup-password";

const TERMS_CHECKBOX_CLASS = "signup-form__terms-checkbox";
const COOKIE_CHECKBOX_CLASS = "signup-form__cookie-checkbox";

const SignupForm = ({ setView, isMobile = false }) => {
  const { __ } = useTranslation();
  const dispatch = useDispatch();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [termsChecked, setTermsChecked] = useState(false);
  const [cookieChecked, setCookieChecked] = useState(false);
  const [, setModalActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleLegalLinkClick = (e) => {
    e.stopPropagation();
  };

  const focusInput = (inputId) => {
    const inputElement = document.getElementById(inputId);

    if (inputElement) {
      inputElement.focus();
    }
  };

  const focusCheckbox = (checkboxClassName) => {
    const checkboxElement = document.querySelector(`.${checkboxClassName}`);

    if (!checkboxElement) {
      return;
    }

    const checkboxInput = checkboxElement.matches('input[type="checkbox"]')
      ? checkboxElement
      : checkboxElement.querySelector('input[type="checkbox"]');

    if (checkboxInput) {
      checkboxInput.focus();
    }
  };

  const validateForm = () => {
    if (!email.trim() || !isEmailValid(email)) {
      focusInput(EMAIL_INPUT_ID);
      return false;
    }

    if (!password || !isPasswordValid(password)) {
      focusInput(PASSWORD_INPUT_ID);
      return false;
    }

    if (!termsChecked) {
      focusCheckbox(TERMS_CHECKBOX_CLASS);
      return false;
    }

    if (!cookieChecked) {
      focusCheckbox(COOKIE_CHECKBOX_CLASS);
      return false;
    }

    return true;
  };

  const handleGoogleAuth = () => {
    if (loading) {
      return;
    }

    setLoading(true);

    const p_hash = localStorage.getItem("p_hash");
    const query = p_hash ? `?p_hash=${encodeURIComponent(p_hash)}` : "";

    window.location.href = `${GOOGLE_AUTH_URL}${query}`;
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const p_hash = localStorage.getItem("p_hash");

    const registrationData = {
      email,
      password,
    };

    if (p_hash) {
      registrationData.p_hash = p_hash;
    }

    dispatch(registration(registrationData))
      .then(() => {
        setModalActive(email);
        setSuccess(true);

        if (p_hash) {
          localStorage.removeItem("p_hash");
        }
      })
      .catch((error) => {
        throw new Error("Error response:", error?.response?.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (success) {
    return (
      <div className={`auth-form${isMobile ? " auth-form--mobile" : ""}`}>
        <div
          className={`auth-form__form${
            isMobile ? " auth-form__form--mobile" : ""
          }`}
          style={{
            textAlign: "center",
            justifyContent: "center",
          }}
        >
          {!isMobile && <BinifyLogo width={"98"} height={"18"} />}

          <div
            style={{
              marginTop: isMobile ? "20px" : "40px",
              marginBottom: "20px",
            }}
          >
            <h2
              className={`auth-form__title${
                isMobile ? " auth-form__title--mobile" : ""
              }`}
              style={{ marginBottom: "16px" }}
            >
              {__("auth.check_in_box1")}
            </h2>

            <p
              className={`auth-form__text${
                isMobile ? " auth-form__text--mobile" : ""
              }`}
              style={{ lineHeight: "1.5" }}
            >
              {__("auth.check_in_box2_1")}
              <br />

              <strong
                style={{
                  color: "#333",
                  display: "block",
                  marginTop: "8px",
                }}
              >
                {email}
              </strong>
            </p>

            <p
              className={`auth-form__text${
                isMobile ? " auth-form__text--mobile" : ""
              }`}
              style={{
                marginTop: "16px",
                fontSize: "14px",
                color: "#666",
              }}
            >
              {__("auth.check_in_box2_2")}
            </p>
          </div>

          <FormButton
            variant="secondary"
            type="button"
            className={`auth-form__button-wrapper auth-form__button-wrapper--uppercase${
              isMobile ? " auth-form__button-wrapper--mobile" : ""
            }`}
            onClick={() => setView("login")}
          >
            {__("auth.logIn")}
          </FormButton>
        </div>
      </div>
    );
  }

  return (
    <div
      data-form="signup-form"
      className={`auth-form${isMobile ? " auth-form--mobile" : ""}`}
    >
      <form
        className={`auth-form__form${
          isMobile ? " auth-form__form--mobile" : ""
        }`}
        onSubmit={onSubmit}
        noValidate
      >
        {!isMobile && <BinifyLogo width={"98"} height={"18"} />}

        <h1
          className={`auth-form__title${
            isMobile ? " auth-form__title--mobile" : ""
          }`}
        >
          {__("auth.signUp")}
        </h1>

        {!isMobile && (
          <GoogleButton
            variant="light"
            type="button"
            className="auth-form__google-button"
            isSignUp={true}
            onClick={handleGoogleAuth}
            disabled={loading}
          />
        )}

        {!isMobile && (
          <div className="auth-form__divider-wrapper">
            <div className="auth-form__divider-line"></div>

            <span className="auth-form__divider-text">
              {__("auth.signUpWithEmail")}
            </span>
          </div>
        )}

        <Input
          id={EMAIL_INPUT_ID}
          label={__("auth.emailAddress")}
          showRequiredMark={true}
          type="email"
          message={
            getEmailMessage(email) && email.length > 0
              ? __("auth.pleaseEnterValidEmail")
              : ""
          }
          messageStatus="error"
          status={getEmailStatus(email)}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`auth-form__input-wrapper${
            isMobile ? " auth-form__input-wrapper--mobile" : ""
          }`}
        />

        <Input
          id={PASSWORD_INPUT_ID}
          label={__("auth.password")}
          showRequiredMark={true}
          type="password"
          message={
            getPasswordMessage(password) ? __("auth.passwordMinLength") : ""
          }
          status={getPasswordStatus(password)}
          messageStatus={getPasswordStatus(password)}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={`auth-form__input-wrapper${
            isMobile
              ? " auth-form__input-wrapper--mobile auth-form__input-wrapper--password-mobile"
              : " auth-form__input-wrapper--password"
          }`}
        />

        <Checkbox
          className={`auth-form__checkbox-wrapper ${TERMS_CHECKBOX_CLASS}${
            isMobile ? " auth-form__checkbox-wrapper--mobile" : ""
          }`}
          checked={termsChecked}
          onChange={(e) => setTermsChecked(e.target.checked)}
        >
          {__("auth.agreeTerms")}{" "}
          <a
            href={LEGAL_LINKS.termsOfUse}
            className="auth-form__link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLegalLinkClick}
          >
            {__("main.footer.termsOfUse")}
          </a>{" "}
          {__("auth.and")}{" "}
          <a
            href={LEGAL_LINKS.privacyPolicy}
            className="auth-form__link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLegalLinkClick}
          >
            {__("main.footer.privacyPolicy")}
          </a>
        </Checkbox>

        <Checkbox
          className={`auth-form__checkbox-wrapper ${COOKIE_CHECKBOX_CLASS}${
            isMobile
              ? " auth-form__checkbox-wrapper--mobile auth-form__checkbox-wrapper--last-mobile"
              : " auth-form__checkbox-wrapper--last"
          }`}
          checked={cookieChecked}
          onChange={(e) => setCookieChecked(e.target.checked)}
        >
          {__("auth.agreeTerms")}{" "}
          <a
            href={LEGAL_LINKS.cookiePolicy}
            className="auth-form__link"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLegalLinkClick}
          >
            {__("main.footer.cookiePolicy")}
          </a>
        </Checkbox>

        <FormButton
          variant="secondary"
          type="submit"
          className={`auth-form__button-wrapper auth-form__button-wrapper--uppercase${
            isMobile ? " auth-form__button-wrapper--mobile" : ""
          }`}
        >
          {loading ? __("auth.loading") || "Loading..." : __("auth.signUpBtn")}
        </FormButton>

        {isMobile && (
          <>
            <div className="auth-form__divider-wrapper auth-form__divider-wrapper--mobile">
              <div className="auth-form__divider-line"></div>

              <span className="auth-form__divider-text">
                {__("auth.signUpWith")}
              </span>
            </div>

            <GoogleButton
              variant="light"
              type="button"
              className="auth-form__google-button auth-form__google-button--mobile"
              isSignUp={true}
              onClick={handleGoogleAuth}
              disabled={loading}
            />
          </>
        )}

        <p
          className={`auth-form__text${
            isMobile ? " auth-form__text--mobile" : ""
          }`}
        >
          {__("auth.alreadyHaveAccount")}{" "}
          <a
            href="#"
            className="auth-form__link"
            onClick={(e) => {
              e.preventDefault();
              setView("login");
            }}
          >
            {__("auth.signIn")}
          </a>
        </p>
      </form>
    </div>
  );
};

export default SignupForm;
